import { LitElement, html, nothing, type CSSResultGroup, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import styles from './styles.js';
import { deepQueryById } from '../utils/dom.utils.js';

// Structural type for the native CommandEvent constructor (Baseline newly
// available, Dec 2025) so we can use it where present without TS lib deps.
type CommandEventCtor = new (type: string, init: { command: string; source?: Element | null } & EventInit) => Event;

export type ButtonVariant  = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'help' | 'danger' | 'contrast';
export type ButtonSize     = 'small' | 'medium' | 'large';
export type ButtonIconPos  = 'left' | 'right' | 'top' | 'bottom';
export type ButtonType     = 'button' | 'submit' | 'reset';

/**
 * uwc-button
 *
 * Feature-rich button component.
 *
 * @attr label          - Text label (alternative to default slot).
 * @attr variant        - Visual variant. Default: primary.
 * @attr size           - small | medium | large. Default: medium.
 * @attr icon           - uwc-icon name rendered inside the button.
 * @attr icon-pos       - left | right | top | bottom. Default: left.
 * @attr icon-only      - Square icon-only button; set aria-label for a11y.
 * @attr type           - Native button type: button | submit | reset. Default: button.
 * @attr command        - Invoker Command forwarded to the native button (e.g. show-modal, toggle-popover).
 * @attr commandfor     - Id of the element the command targets. Degrades to no-op where unsupported.
 * @attr raised         - Add elevation shadow.
 * @attr rounded        - Fully rounded pill / circle shape.
 * @attr outline        - Border-only style (no fill).
 * @attr text           - Ghost / text-only style.
 * @attr link           - Render as an anchor-styled button.
 * @attr fluid          - Stretch to fill container width.
 * @attr loading        - Show spinner and disable interaction.
 * @attr loading-icon   - Custom icon name for loading state. Default: arrow-clockwise.
 * @attr disabled       - Disable the button.
 * @attr badge          - Badge text rendered on trailing edge.
 * @attr badge-severity - Badge color variant. Default: danger.
 * @attr aria-label     - Accessible label (required for icon-only buttons).
 * @attr tabindex       - Tab order.
 * @attr autofocus      - Auto-focus on mount.
 *
 * @slot default  - Label content (overridden by label attribute).
 * @slot prefix   - Content before the label (icon, avatar, etc.).
 * @slot suffix   - Content after the label (icon, badge, etc.).
 *
 * @fires uwc-focus - Fired when the button gains focus.
 * @fires uwc-blur  - Fired when the button loses focus.
 * @fires uwc-click - Fired on click (not fired when disabled or loading).
 *
 * @csspart button  - The inner <button> element.
 * @csspart label   - The label span.
 * @csspart icon    - The icon wrapper.
 * @csspart badge   - The badge element.
 *
 * @cssprop --uwc-btn-bg           - Button background.
 * @cssprop --uwc-btn-color        - Button text/icon color.
 * @cssprop --uwc-btn-border       - Button border.
 * @cssprop --uwc-btn-radius       - Border radius.
 * @cssprop --uwc-btn-shadow       - Box shadow (raised).
 * @cssprop --uwc-btn-font-size    - Font size.
 * @cssprop --uwc-btn-padding-x    - Horizontal padding.
 * @cssprop --uwc-btn-padding-y    - Vertical padding.
 * @cssprop --uwc-btn-gap          - Gap between icon and label.
 * @cssprop --uwc-btn-transition   - Transition string.
 * @cssprop --uwc-btn-badge-bg     - Badge background color.
 * @cssprop --uwc-btn-badge-color  - Badge text color.
 */
export class UwcButton extends LitElement {

  static override styles: CSSResultGroup = [styles];

  // ── Core ─────────────────────────────────────────────────────────

  @property()                                              label?: string;
  @property({ reflect: true })                             variant:    ButtonVariant = 'primary';
  @property({ reflect: true })                             size:       ButtonSize    = 'medium';
  @property({ reflect: true })                             type:       ButtonType    = 'button';

  // Native Invoker Commands — forwarded to the inner <button>. In browsers that
  // support them (`command`/`commandfor`) this gives declarative dialog/popover
  // control with no JS; elsewhere the attributes are inert (no regression).
  @property()                                              command?:    string;
  @property()                                              commandfor?: string;

  // ── Icon ─────────────────────────────────────────────────────────

  @property()                                              icon?: string;
  @property({ reflect: true, attribute: 'icon-pos' })      iconPos:    ButtonIconPos = 'left';
  @property({ type: Boolean, reflect: true, attribute: 'icon-only' }) iconOnly = false;

  // ── Loading ───────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true })              loading = false;
  @property({ attribute: 'loading-icon' })                 loadingIcon = 'arrow-clockwise';

  // ── Style modifiers ───────────────────────────────────────────────

  @property({ type: Boolean, reflect: true })              raised   = false;
  @property({ type: Boolean, reflect: true })              rounded  = false;
  @property({ type: Boolean, reflect: true })              outline  = false;
  @property({ type: Boolean, reflect: true })              text     = false;
  @property({ type: Boolean, reflect: true })              link     = false;
  @property({ type: Boolean, reflect: true })              fluid    = false;
  @property({ type: Boolean, reflect: true })              disabled = false;

  // ── Badge ─────────────────────────────────────────────────────────

  @property()                                              badge?: string;
  @property({ attribute: 'badge-severity' })               badgeSeverity = 'danger';

  // ── Accessibility ─────────────────────────────────────────────────

  @property({ attribute: 'aria-label' })                   override ariaLabel: string | null = null;
  @property({ type: Number })                              tabindex?: number;
  @property({ type: Boolean })                             override autofocus = false;

  // ── Helpers ───────────────────────────────────────────────────────

  private _hasSlot(name: string): boolean {
    return Array.from(this.children).some(el => (el as HTMLElement).slot === name);
  }

  private _iconSize(): string {
    // em-relative: the icon tracks the button's per-size font-size (which itself
    // scales with the global Theme Designer scale), so one value covers every size.
    return '1.1em';
  }

  // ── Ripple ───────────────────────────────────────────────────────

  private _handleClick(e: MouseEvent): void {
    if (this.disabled || this.loading) return;
    this._spawnRipple(e);
    this._invokeCustomCommand();
    this.dispatchEvent(new CustomEvent('uwc-click', { bubbles: true, composed: true, detail: { originalEvent: e } }));
  }

  /** A custom Invoker Command — one whose name starts with `--`. */
  private get _isCustomCommand(): boolean {
    return !!this.command && this.command.startsWith('--');
  }

  /**
   * Dispatch custom Invoker Commands (`command="--foo"`) ourselves.
   *
   * The native `commandfor` attribute resolves its target by id within the
   * invoker's own root — but our `<button>` lives in this element's shadow
   * root, so it can never reach a target in the light DOM (the common case).
   * Custom commands also have no built-in UA behaviour, so the only thing that
   * makes them work is firing a `CommandEvent` on the target. Doing it here:
   *   • works across shadow boundaries (we deep-resolve the id), and
   *   • doubles as the fallback for browsers without the Invoker Commands API.
   *
   * Built-in commands (e.g. `show-modal`, `toggle-popover`) are NOT handled
   * here — they remain forwarded to the native button via the `command` /
   * `commandfor` attributes in render().
   */
  private _invokeCustomCommand(): void {
    if (!this._isCustomCommand || !this.commandfor) return;

    const target = deepQueryById(document, this.commandfor);
    if (!target) return;

    const source = this.shadowRoot?.querySelector<HTMLButtonElement>('[part="button"]') ?? null;
    const CommandEventImpl = (globalThis as { CommandEvent?: CommandEventCtor }).CommandEvent;

    const event = CommandEventImpl
      ? new CommandEventImpl('command', {
          command: this.command!,
          source,
          bubbles: true,
          composed: true,
          cancelable: true,
        })
      : Object.assign(
          // Fallback shim for browsers without the Invoker Commands API — same
          // shape consumers read (`event.command`, `event.source`).
          new CustomEvent('command', { bubbles: true, composed: true, cancelable: true }),
          { command: this.command, source },
        );

    target.dispatchEvent(event);
  }

  private _spawnRipple(e: MouseEvent): void {
    const btn  = this.shadowRoot!.querySelector<HTMLElement>('[part="button"]');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x    = e.clientX - rect.left - size / 2;
    const y    = e.clientY - rect.top  - size / 2;
    const span = document.createElement('span');
    span.className = 'uwc-btn-ripple';
    span.style.cssText = `width:${size}px;height:${size}px;left:${x}px;top:${y}px`;
    btn.appendChild(span);
    span.addEventListener('animationend', () => span.remove(), { once: true });
  }

  // ── Render ────────────────────────────────────────────────────────

  private _renderIcon(): TemplateResult {
    const name = this.loading ? this.loadingIcon : this.icon!;
    return html`
      <uwc-icon
        part="icon"
        class="uwc-btn__icon"
        name=${name}
        size=${this._iconSize()}
        ?spin=${this.loading}>
      </uwc-icon>`;
  }

  private _renderLabel(): TemplateResult {
    if (this.label) {
      return html`<span part="label" class="uwc-btn__label">${this.label}</span>`;
    }
    return html`<slot part="label" class="uwc-btn__label"></slot>`;
  }

  private _renderBadge(): TemplateResult | typeof nothing {
    if (!this.badge) return nothing;
    return html`<span part="badge" class="uwc-btn__badge uwc-btn__badge--${this.badgeSeverity}">${this.badge}</span>`;
  }

  override render() {
    const showIcon   = (this.icon && !this.loading) || this.loading;
    const isVertical = this.iconPos === 'top' || this.iconPos === 'bottom';

    const classes = classMap({
      'uwc-btn'                       : true,
      [`uwc-btn--${this.variant}`]    : true,
      [`uwc-btn--${this.size}`]       : true,
      'uwc-btn--raised'               : this.raised,
      'uwc-btn--rounded'              : this.rounded,
      'uwc-btn--outline'              : this.outline,
      'uwc-btn--text'                 : this.text,
      'uwc-btn--link'                 : this.link,
      'uwc-btn--fluid'                : this.fluid,
      'uwc-btn--icon-only'            : this.iconOnly,
      'uwc-btn--icon-top'             : isVertical && this.iconPos === 'top',
      'uwc-btn--icon-bottom'          : isVertical && this.iconPos === 'bottom',
      'uwc-btn--loading'              : this.loading,
      'uwc-btn--disabled'             : this.disabled || this.loading,
    });

    // Assemble children in icon-pos order
    const iconEl   = showIcon          ? this._renderIcon()  : nothing;
    const labelEl  = this._renderLabel();
    const prefixEl = !this.loading && this._hasSlot('prefix')
      ? html`<slot name="prefix" part="icon" class="uwc-btn__icon"></slot>`
      : nothing;
    const suffixEl = this._hasSlot('suffix')
      ? html`<slot name="suffix" part="icon" class="uwc-btn__icon"></slot>`
      : nothing;
    const badgeEl  = this._renderBadge();

    let inner: TemplateResult;
    if (this.iconPos === 'right') {
      inner = html`${prefixEl}${labelEl}${iconEl}${suffixEl}${badgeEl}`;
    } else if (this.iconPos === 'top') {
      inner = html`${iconEl}${labelEl}${prefixEl}${suffixEl}${badgeEl}`;
    } else if (this.iconPos === 'bottom') {
      inner = html`${labelEl}${iconEl}${prefixEl}${suffixEl}${badgeEl}`;
    } else {
      // left (default)
      inner = html`${prefixEl}${iconEl}${labelEl}${suffixEl}${badgeEl}`;
    }

    return html`
      <button
        part="button"
        class=${classes}
        type=${this.type}
        command=${ifDefined(this._isCustomCommand ? undefined : this.command)}
        commandfor=${ifDefined(this._isCustomCommand ? undefined : this.commandfor)}
        ?disabled=${this.disabled || this.loading}
        aria-label=${ifDefined(this.ariaLabel ?? this.label)}
        aria-busy=${this.loading ? 'true' : 'false'}
        tabindex=${ifDefined(this.tabindex)}
        ?autofocus=${this.autofocus}
        @click=${this._handleClick}
        @focus=${() => this.dispatchEvent(new CustomEvent('uwc-focus', { bubbles: true, composed: true }))}
        @blur=${()  => this.dispatchEvent(new CustomEvent('uwc-blur',  { bubbles: true, composed: true }))}
      >
        ${inner}
      </button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-button': UwcButton; }
}
